import type { InputState } from "@neon-arena/shared";
import { getBindings } from "./Bindings.js";

const STICK_DEADZONE = 0.18;
const TRIGGER_DEADZONE = 0.06;

export interface GamepadSnapshot {
  state: InputState;
  active: boolean;
  startPressed: boolean;
}

export class GamepadInput {
  private playerIndex: number;
  private prevButtons: boolean[] = [];
  private prevCameraModeBtn = false;

  constructor(playerIndex = 0) {
    this.playerIndex = playerIndex;
  }

  private getPad(): Gamepad | null {
    if (typeof navigator === "undefined" || !navigator.getGamepads) return null;
    const pads = navigator.getGamepads();
    let seen = 0;
    for (const p of pads) {
      if (p && p.connected) {
        if (seen === this.playerIndex) return p;
        seen++;
      }
    }
    return null;
  }

  /**
   * Right stick + camera action buttons — always separate from game InputState.
   * lookBehind: hold state of the LOOK_BEHIND binding.
   * cameraModePressed: rising edge of the CAMERA_TOGGLE binding.
   */
  pollCamera(): { rx: number; ry: number; lookBehind: boolean; cameraModePressed: boolean } {
    const p = this.getPad();
    if (!p) {
      this.prevCameraModeBtn = false;
      return { rx: 0, ry: 0, lookBehind: false, cameraModePressed: false };
    }
    const gp = getBindings().gamepad;
    const lookBehind = gp.LOOK_BEHIND ? readDigital(p, gp.LOOK_BEHIND) : false;
    const camToggleNow = gp.CAMERA_TOGGLE ? readDigital(p, gp.CAMERA_TOGGLE) : false;
    const cameraModePressed = camToggleNow && !this.prevCameraModeBtn;
    this.prevCameraModeBtn = camToggleNow;
    return {
      rx: applyDeadzone(p.axes[2] ?? 0, STICK_DEADZONE),
      ry: applyDeadzone(p.axes[3] ?? 0, STICK_DEADZONE),
      lookBehind,
      cameraModePressed,
    };
  }

  poll(): GamepadSnapshot {
    const pad = this.getPad();
    if (!pad) return { state: empty(), active: false, startPressed: false };

    const gp = getBindings().gamepad;

    const throttle  = readAnalog(pad, gp.MOVE_FORWARD);
    const brake     = readAnalog(pad, gp.MOVE_BACKWARD);
    const steerL    = readAnalog(pad, gp.TURN_LEFT);
    const steerR    = readAnalog(pad, gp.TURN_RIGHT);
    const steer     = steerL - steerR; // positive = turn left
    const handbrake = readDigital(pad, gp.HANDBRAKE);
    const fire      = readDigital(pad, gp.FIRE);
    const fire2     = readDigital(pad, gp.FIRE2);
    const boost     = readDigital(pad, gp.BOOST);
    const dropWeapon = readDigital(pad, gp.DROP_WEAPON);

    // Start/Menu (btn9) is not rebindable — meta action
    const startBtn = pad.buttons[9]?.pressed ?? false;
    const startPressed = startBtn && !(this.prevButtons[9] ?? false);
    this.prevButtons = pad.buttons.map((b) => b.pressed);

    const active =
      throttle > 0.02 || brake > 0.02 || Math.abs(steer) > 0.01 ||
      handbrake || fire || fire2 || boost || dropWeapon;

    return {
      state: { throttle, brake, steer, handbrake, fire, fire2, boost, dropWeapon, yaw: 0 },
      active,
      startPressed,
    };
  }
}

/** Read a binding as a 0..1 analog value. Works for buttons (incl. analog triggers) and axes. */
export function readAnalog(pad: Gamepad, binding: string): number {
  if (!binding) return 0;
  if (binding.startsWith("btn")) {
    const idx = parseInt(binding.slice(3));
    const btn = pad.buttons[idx];
    if (!btn) return 0;
    return applyDeadzone(btn.value, TRIGGER_DEADZONE);
  }
  if (binding.startsWith("axis")) {
    const positive = binding.endsWith("+");
    const idx = parseInt(binding.slice(4, -1));
    const v = pad.axes[idx] ?? 0;
    const side = positive ? Math.max(0, v) : Math.max(0, -v);
    return applyDeadzone(side, STICK_DEADZONE);
  }
  return 0;
}

export function readDigital(pad: Gamepad, binding: string): boolean {
  return readAnalog(pad, binding) > 0.5;
}

function applyDeadzone(v: number, dz: number): number {
  if (Math.abs(v) < dz) return 0;
  const sign = Math.sign(v);
  return sign * ((Math.abs(v) - dz) / (1 - dz));
}

function empty(): InputState {
  return { throttle: 0, brake: 0, steer: 0, handbrake: false, fire: false, boost: false, dropWeapon: false, yaw: 0 };
}
